<template>
  <div>
    <!-- ── Sección 1: Información general ── -->
    <v-form ref="roleForm" lazy-validation>
      <h4 class="mb-3">
        <v-icon left small color="primary">mdi-information-outline</v-icon>
        Información del modificador
      </h4>

      <v-row>
        <v-col md="8" cols="12">
          <v-text-field
            v-model="data.Nombre"
            :rules="rules.Nombre"
            label="Nombre del modificador"
            placeholder="Ej. Tamaño, Temperatura, Extras…"
            hint="Nombre visible para el cliente"
            persistent-hint
            outlined
            dense
            counter="60"
            maxlength="60"
            prepend-inner-icon="mdi-tag-text-outline"
            required
          />

          <v-autocomplete
            v-model="data.Departamento"
            :rules="rules.Departamento"
            label="Departamento"
            placeholder="Seleccione un departamento"
            hint="Área a la que pertenece este modificador"
            persistent-hint
            :items="modulos"
            item-text="text"
            item-value="value"
            outlined
            dense
            prepend-inner-icon="mdi-store-outline"
            no-data-text="No hay departamentos disponibles"
            class="mt-2"
          />
        </v-col>

        <v-col md="4" cols="12">

          <v-card outlined rounded class="pa-3">
            <v-radio-group
              v-model="data.Tipo"
              :rules="rules.Tipo"
              mandatory
              hide-details
              class="mt-0"
            >
              <v-radio value="S">
                <template #label>
                  <div>
                    <strong>Selección única</strong>
                    <div class="caption grey--text">El cliente elige solo una opción</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="M" class="mt-2">
                <template #label>
                  <div>
                    <strong>Selección múltiple</strong>
                    <div class="caption grey--text">El cliente puede elegir varias opciones</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <v-divider class="my-4" />

    <!-- ── Sección 2: Opciones ── -->
    <v-row>
      <!-- Formulario de nueva opción -->
      <v-col md="4" cols="12">
        <h4 class="mt-1 mb-3">
          <v-icon left small color="primary">mdi-plus-circle-outline</v-icon>
          Agregar opción
        </h4>

        <v-form ref="opcionForm" lazy-validation>
          <v-text-field
            v-model="opcionData.nam"
            :rules="rulesOpcion.nam"
            label="Nombre de la opción"
            placeholder="Ej. Grande, Sin cebolla…"
            outlined
            dense
            counter="50"
            maxlength="50"
            prepend-inner-icon="mdi-label-outline"
            required
          />

          <v-text-field
            v-model="opcionData.pco.C"
            :rules="rulesOpcion.pco"
            :label="`Precio adicional (${opcionData.pco.M})`"
            placeholder="0.00"
            hint="Usa 0 si no tiene costo adicional"
            persistent-hint
            outlined
            dense
            prepend-inner-icon="mdi-currency-usd"
            @keypress="$pgsHelper.onlyNumber($event, opcionData.pco.C)"
            required
          />

          <v-autocomplete
            v-model="opcionData.ref.id"
            :items="comp_prod"
            item-text="text"
            item-value="id"
            label="Producto de referencia"
            hint="Opcional — vincula un producto de inventario"
            persistent-hint
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-package-variant-closed"
            no-data-text="No hay productos disponibles"
            class="mt-2"
          />

          <v-text-field
            v-if="opcionData.ref.id"
            v-model="opcionData.ref.cnt"
            :rules="rulesOpcion.cnt"
            label="Cantidad del producto"
            placeholder="1"
            outlined
            dense
            prepend-inner-icon="mdi-numeric"
            @keypress="$pgsHelper.onlyNumber($event, opcionData.ref.cnt)"
            required
            class="mt-2"
          />

          <v-btn
            color="primary"
            outlined
            block
            @click="addOpcion"
            class="mt-2"
          >
            <v-icon left>mdi-plus</v-icon>
            Agregar opción
          </v-btn>
        </v-form>
      </v-col>

      <!-- Lista de opciones agregadas -->
      <v-col md="8" cols="12">
        <div class="d-flex align-center justify-space-between mt-1 mb-3">
          <h4>
            <v-icon left small color="primary">mdi-format-list-bulleted</v-icon>
            Opciones del modificador
          </h4>
          <v-chip small :color="data.Opciones.length > 0 ? 'success' : 'grey'" dark>
            {{ data.Opciones.length }} {{ data.Opciones.length === 1 ? 'opción' : 'opciones' }}
          </v-chip>
        </div>

        <!-- Alerta si no hay opciones -->
        <v-alert
          v-if="data.Opciones.length === 0"
          type="info"
          outlined
          dense
          class="caption"
          icon="mdi-information-outline"
        >
          Agrega al menos una opción para poder guardar el modificador.
        </v-alert>

        <!-- Error al intentar guardar sin opciones -->
        <v-alert
          v-if="sinOpciones"
          type="error"
          dense
          outlined
          dismissible
          class="caption"
          @input="sinOpciones = false"
        >
          Debes agregar al menos una opción antes de guardar.
        </v-alert>

        <v-list v-if="data.Opciones.length > 0" dense>
          <template v-for="(item, i) in data.Opciones">
            <v-divider :key="'d' + i" />
            <v-list-item :key="i">
              <v-list-item-icon class="mr-2 mt-3">
                <v-icon small color="grey">mdi-circle-small</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ item.nam }}</v-list-item-title>
                <v-list-item-subtitle>
                  Precio adicional:
                  <strong>${{ Number(item.pco.C).toFixed(2) }}</strong>
                  <span v-if="item.ref && item.ref.id" class="ml-2">
                    · Ref: {{ getRefNombre(item.ref.id) }}
                    ({{ item.ref.cnt }} {{ item.ref.cnt == 1 ? 'unidad' : 'unidades' }})
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      color="red lighten-1"
                      @click="deleteOpcion(i)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar opción</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-col>
    </v-row>

    <v-divider class="my-4" />

    <!-- ── Acciones ── -->
    <div class="d-flex justify-end gap-2">
      <v-btn
        text
        @click="$emit('toggle', false)"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="success"
        depressed
        class="ml-2"
        @click="checkForm"
      >
        <v-icon left>mdi-content-save-outline</v-icon>
        Guardar modificador
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
</style>

<script>
import Modificadores from "../../../clases/Modificadores.js";
import Currencies from "../../../clases/Currencies.js";
import { V } from "@/helpers/ValidationRules";

export default {
  name: "ModificadoresForm",
  components: {},
  props: ["edit", "dataEdit"],
  data() {
    return {
      familias: [],
      sinOpciones: false,
      data: new Modificadores(),

      // Reglas del modificador usando V de ValidationRules
      rules: {
        Nombre: [V.required("El nombre del modificador es obligatorio"), V.maxLength(60)],
        Departamento: [V.requiredSelect("Debes seleccionar un departamento")],
        Tipo: [V.required("Debes seleccionar un tipo de selección")],
      },

      // Reglas de cada opción usando V de ValidationRules
      rulesOpcion: {
        nam: [
          V.required("El nombre de la opción es obligatorio"),
          V.maxLength(50),
        ],
        pco: [
          (v) => (v !== null && v !== undefined && v !== "") || "El precio es obligatorio",
          V.numeric("Debe ser un número válido"),
          V.nonNegative("El precio no puede ser negativo"),
        ],
        cnt: [
          V.required("La cantidad es obligatoria"),
          V.numeric("Debe ser un número válido"),
          V.positiveNumber("La cantidad debe ser mayor a 0"),
        ],
      },

      opcionData: {
        nam: "",
        pco: { C: 0, M: Currencies.getCurrencyDefault() },
        ref: { id: null, or: true, cnt: 1 },
        refp: null,
        ori: true,
      },
    };
  },
  computed: {
    modulos() {
      let dep = [{ value: "", text: "Seleccione un módulo" }];
      let departamentos = this.$store.getters.getPDVDepartamentos;
      departamentos.forEach((element) => {
        dep.push({ value: element.id, text: element.Nombre });
      });
      return dep;
    },
    comp_prod() {
      let producto = this.$store.getters.getProductos;
      let prod = [{ id: null, text: "Sin referencia" }];
      producto.forEach((ele) => {
        if (ele.Tipo != "Servicio") {
          prod.push({ id: ele.id, text: ele.Nombre });
        }
      });
      return prod;
    },
  },
  methods: {
    checkForm() {
      const formValid = this.$refs.roleForm.validate();
      if (!formValid) return;

      if (this.data.Opciones.length === 0) {
        this.sinOpciones = true;
        return;
      }

      this.sinOpciones = false;
      this.send(this.edit ? "EDIT" : "CREATE");
    },

    addOpcion() {
      if (!this.$refs.opcionForm.validate()) return;

      const nuevaOpcion = this.$pgsHelper.cloneObj(this.opcionData);
      nuevaOpcion.pco.C = Number(nuevaOpcion.pco.C);

      if (nuevaOpcion.ref && nuevaOpcion.ref.id) {
        nuevaOpcion.ref.cnt = Number(nuevaOpcion.ref.cnt);
      } else {
        nuevaOpcion.ref = null;
      }

      this.data.addOpcion(nuevaOpcion);

      // Limpiar formulario de opción
      this.opcionData.nam = "";
      this.opcionData.pco.C = 0;
      this.opcionData.ref = { id: null, or: true, cnt: 1 };
      this.$refs.opcionForm.resetValidation();
      this.$forceUpdate();
    },

    deleteOpcion(index) {
      this.data.deleteOpcion(index);
    },

    getRefNombre(id) {
      const found = this.comp_prod.find((p) => p.id === id);
      return found ? found.text : "—";
    },

    send(action) {
      this.$emit("successData", this.data, action);
    },

    async getFamilias() {
      const data = await this.$store.getters.getFirebaseApp.database.queryBuilder(
        "PDV/Productos/Familias"
      );
      this.familias = [];
      this.familias = this.$pgsHelper.ArrayObjectToArray(data.val());
    },
  },
  async mounted() {
    if (this.edit) {
      this.dataEdit.id = this.dataEdit.Id;
      this.data = new Modificadores(this.dataEdit);
    }
  },
};
</script>
